// Location persistence service for storing and retrieving last known location
// Uses localStorage on web, will use native storage on mobile platforms

import { logger } from '../../utils/logger.js'

const log = logger.create('location')

export interface StoredLocation {
  lat: number
  lng: number
  zoom?: number
  timestamp: number
  accuracy?: number
  source: 'gps' | 'manual' | 'cached'
}

export class LocationPersistenceService {
  private static readonly STORAGE_KEY = 'cairn_last_location'
  private static readonly MAX_AGE_MS = 24 * 60 * 60 * 1000 // 24 hours

  /**
   * Store the current location for future use
   */
  static storeLocation(location: StoredLocation): void {
    try {
      const locationData = {
        ...location,
        timestamp: Date.now(),
      }

      if (this.isCapacitorAvailable()) {
        // TODO: Use Capacitor Preferences for native storage
        // import { Preferences } from '@capacitor/preferences'
        // await Preferences.set({ key: this.STORAGE_KEY, value: JSON.stringify(locationData) })
      }

      // Fallback to localStorage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(locationData))

      log.debug('📍 Location stored:', {
        lat: location.lat.toFixed(6),
        lng: location.lng.toFixed(6),
        source: location.source,
      })
    } catch (error) {
      log.warn('Failed to store location:', error)
    }
  }

  /**
   * Retrieve the last known location if still valid
   */
  static getLastLocation(): StoredLocation | null {
    try {
      let storedData: string | null = null

      if (this.isCapacitorAvailable()) {
        // TODO: Use Capacitor Preferences for native storage
        // const result = await Preferences.get({ key: this.STORAGE_KEY })
        // storedData = result.value
      }

      // Fallback to localStorage
      storedData = localStorage.getItem(this.STORAGE_KEY)

      if (!storedData) {
        return null
      }

      const location: StoredLocation = JSON.parse(storedData)

      // Check if location is still valid (not too old)
      const age = Date.now() - location.timestamp
      if (age > this.MAX_AGE_MS) {
        log.debug('📍 Stored location expired, age:', Math.round(age / (60 * 60 * 1000)), 'hours')
        this.clearLocation()
        return null
      }

      log.debug('📍 Using stored location:', {
        lat: location.lat.toFixed(6),
        lng: location.lng.toFixed(6),
        age: Math.round(age / (60 * 1000)),
        source: location.source,
      })

      return location
    } catch (error) {
      log.warn('Failed to retrieve stored location:', error)
      return null
    }
  }

  /**
   * Clear stored location
   */
  static clearLocation(): void {
    try {
      if (this.isCapacitorAvailable()) {
        // TODO: Use Capacitor Preferences
        // await Preferences.remove({ key: this.STORAGE_KEY })
      }

      localStorage.removeItem(this.STORAGE_KEY)
      log.debug('📍 Stored location cleared')
    } catch (error) {
      log.warn('Failed to clear stored location:', error)
    }
  }

  /**
   * Get default fallback location (could be configurable)
   */
  static getDefaultLocation(): StoredLocation {
    // Default to London as a reasonable starting point
    // In production, this could be user-configurable or based on IP geolocation
    return {
      lat: 51.505,
      lng: -0.09,
      zoom: 13,
      timestamp: 0,
      source: 'manual',
    }
  }

  /**
   * Get the best available location (stored > default)
   */
  static getBestLocation(): StoredLocation {
    return this.getLastLocation() ?? this.getDefaultLocation()
  }

  private static isCapacitorAvailable(): boolean {
    return !!(window as unknown as Record<string, unknown>).Capacitor
  }
}
